import { Link } from 'react-router-dom';
import InlineCTA from './InlineCTA';
import BlogImage from '../components/BlogImage';

export default function ParlayCalculatorTutorial() {
  return (
    <article style={{ maxWidth: 820, margin: '0 auto', padding: '40px 20px', color: '#e0e0e0', fontFamily: 'sans-serif', lineHeight: 1.8 }}>

      <p style={{ color: '#999', fontSize: 14, marginBottom: 32 }}>
        最後更新：2026-03-18 ｜ 閱讀時間：約 18 分鐘
      </p>

      {/* ===== 導讀 ===== */}
      <p>
        串關（Parlay）是運彩中最刺激的玩法之一——用小本金搏大報酬。
        但你知道 2 關到 10 關的理論報酬率差多少嗎？
        實際勝率又是多少？怎樣的串關組合才是最「划算」的？
      </p>
      <p>
        我們開發了免費的<Link to="/tools/parlay-calculator" style={{ color: '#93c5fd' }}>串關計算器</Link>，
        輸入各場比賽的賠率就能即時算出串關總賠率、預期回報、以及勝率預估。
        本文將完整教你如何使用這個工具，並附上 2-10 關的報酬率速查表。
      </p>
      <p>
        如果你對運彩還不熟悉，建議先閱讀<Link to="/blog/sports-betting-guide" style={{ color: '#93c5fd' }}>運彩入門完整指南</Link>。
        想了解串關的進階策略，可以參考<Link to="/blog/parlay-betting-strategy" style={{ color: '#93c5fd' }}>串關策略完整攻略</Link>。
      </p>
      <p>
        <strong>重要聲明：</strong>本文僅供教學與資訊參考，不鼓勵任何形式的賭博行為。
        串關計算器是數學工具，不能預測比賽結果。請理性投注，量力而行。
      </p>

      <BlogImage src="/assets/images/articles/parlay-calculator-tutorial-hero.webp" alt="串關計算器介面：多場比賽賠率輸入區、串關總賠率與預期回報即時顯示" caption="串關計算器——精準掌握每一筆串關的報酬與風險" width={1200} height={675} priority={true} />

      {/* ===== 目錄 ===== */}
      <nav aria-label="文章目錄" style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.15)', borderRadius: 12, padding: '16px 20px', margin: '24px 0' }}>
        <p style={{ color: '#d4af37', fontWeight: 'bold', marginBottom: 12, fontSize: 16 }}>本文目錄</p>
        <ol style={{ paddingLeft: 20, margin: 0, lineHeight: 2 }}>
          <li><a href="#how-parlay-works" style={{ color: '#93c5fd' }}>串關計算原理</a></li>
          <li><a href="#tool-tutorial" style={{ color: '#93c5fd' }}>計算器操作教學</a></li>
          <li><a href="#payout-table" style={{ color: '#93c5fd' }}>2-10 關報酬率速查表</a></li>
          <li><a href="#calculation-examples" style={{ color: '#93c5fd' }}>串關計算實例：2 串 1 到 5 串 1</a></li>
          <li><a href="#optimal-strategy" style={{ color: '#93c5fd' }}>最佳串關組合策略</a></li>
          <li><a href="#risk-management" style={{ color: '#93c5fd' }}>串關風險管理</a></li>
          <li><a href="#parlay-vs-single" style={{ color: '#93c5fd' }}>串關 vs 單場投注的數學比較</a></li>
          <li><a href="#risk-analysis" style={{ color: '#93c5fd' }}>風險分析：關數越多越危險</a></li>
          <li><a href="#common-mistakes" style={{ color: '#93c5fd' }}>常見串關錯誤</a></li>
          <li><a href="#examples" style={{ color: '#93c5fd' }}>實戰計算範例</a></li>
          <li><a href="#faq" style={{ color: '#93c5fd' }}>常見問題 FAQ</a></li>
        </ol>
      </nav>

      {/* ===== 第一章 ===== */}
      <h2 id="how-parlay-works" style={{ color: '#d4af37', marginTop: 48 }}>一、串關計算原理</h2>

      <p>
        串關的計算方式非常直觀：將各場比賽的賠率相乘，就是串關的總賠率。
      </p>

      <div style={{ background: '#111', border: '1px solid #333', borderRadius: 8, padding: 16, margin: '16px 0', fontFamily: 'monospace', fontSize: 14 }}>
        <p style={{ margin: 0 }}>串關總賠率 = 賠率 A x 賠率 B x 賠率 C x ...</p>
        <p style={{ margin: '8px 0 0 0' }}>預期回報 = 投注金額 x 串關總賠率</p>
        <p style={{ margin: '8px 0 0 0' }}>串關勝率 = 勝率 A x 勝率 B x 勝率 C x ...</p>
        <p style={{ margin: '12px 0 0 0', color: '#fbbf24' }}>範例：3 場賠率都是 1.90 的 3 關串</p>
        <p style={{ margin: '4px 0 0 0' }}>總賠率 = 1.90 x 1.90 x 1.90 = 6.859</p>
        <p style={{ margin: '4px 0 0 0' }}>投注 100 元 → 全中可得 685.9 元</p>
      </div>

      <p>
        串關的魅力在於報酬放大效果：單場 1.90 的賠率看似普通，
        但串 3 關就變成 6.86 倍，串 5 關則變成 24.76 倍。
        但代價是勝率也會等比例下降。
      </p>

      <p>
        要注意的是，串關的勝率計算建立在「各場比賽獨立」的假設上。
        以每場勝率 52.6%（賠率 1.90 對應的隱含勝率）為例：
      </p>
      <div style={{ background: '#111', border: '1px solid #333', borderRadius: 8, padding: 16, margin: '16px 0', fontFamily: 'monospace', fontSize: 14 }}>
        <p style={{ margin: 0 }}>2 關串勝率 = 52.6% x 52.6% = 27.7%</p>
        <p style={{ margin: '4px 0 0 0' }}>3 關串勝率 = 52.6%^3 = 14.6%</p>
        <p style={{ margin: '4px 0 0 0' }}>5 關串勝率 = 52.6%^5 = 4.0%</p>
        <p style={{ margin: '4px 0 0 0' }}>10 關串勝率 = 52.6%^10 = 0.16%</p>
        <p style={{ margin: '12px 0 0 0', color: '#f87171' }}>結論：每多一關，勝率就被砍一半左右</p>
      </div>

      {/* ===== 第二章 ===== */}
      <h2 id="tool-tutorial" style={{ color: '#d4af37', marginTop: 48 }}>二、計算器操作教學</h2>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>步驟 1：開啟計算器</h3>
      <p>
        前往<Link to="/tools/parlay-calculator" style={{ color: '#93c5fd' }}>串關計算器</Link>頁面。
        你會看到一個乾淨的介面，預設顯示 2 個投注欄位。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>步驟 2：輸入各場賠率</h3>
      <p>
        在每個欄位中輸入該場比賽的十進制賠率（例如 1.85、2.10）。
        如果需要更多場次，點擊「新增比賽」按鈕即可擴展。
        計算器支援 2-10 關的串關計算。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>步驟 3：輸入投注金額</h3>
      <p>
        在底部的投注金額欄位輸入你打算投注的金額。
        計算器會即時顯示全中時的回收金額。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>步驟 4：查看結果</h3>
      <p>
        計算器會即時顯示：
      </p>
      <ul>
        <li><strong>串關總賠率：</strong>所有賠率相乘的結果</li>
        <li><strong>預期回報：</strong>全中時可獲得的金額</li>
        <li><strong>預估勝率：</strong>根據賠率反推的串關中獎機率</li>
        <li><strong>期望值（EV）：</strong>這筆串關投注的數學期望損益</li>
      </ul>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>步驟 5：調整與比較</h3>
      <p>
        計算器的即時性讓你可以快速比較不同組合。
        試試把某場比賽從讓分改成大小分，看看總賠率和勝率怎麼變化。
        也可以增減場次觀察 EV 的影響。建議每次下注前至少比較 3 種不同組合。
      </p>

      <InlineCTA />

      {/* ===== 第三章 ===== */}
      <h2 id="payout-table" style={{ color: '#d4af37', marginTop: 48 }}>三、2-10 關報酬率速查表</h2>

      <p>
        以下表格假設每場比賽的賠率均為 1.90（運彩最常見的讓分盤賠率），
        投注金額 NT$100：
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, marginTop: 16 }}>
          <thead>
            <tr style={{ background: '#1e40af', color: '#fff' }}>
              <th style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>串關數</th>
              <th style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>總賠率</th>
              <th style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>全中回收</th>
              <th style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>預估勝率</th>
              <th style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>EV（每100元）</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#4ade80' }}>2 關</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>3.61</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>$361</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>27.7%</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#fbbf24' }}>-$0.03</td>
            </tr>
            <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#4ade80' }}>3 關</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>6.86</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>$686</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>14.6%</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#fbbf24' }}>-$0.08</td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#fbbf24' }}>4 關</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>13.03</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>$1,303</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>7.7%</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#fbbf24' }}>-$0.15</td>
            </tr>
            <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#fbbf24' }}>5 關</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>24.76</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>$2,476</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>4.0%</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#fbbf24' }}>-$0.28</td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#f87171' }}>6 關</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>47.05</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>$4,705</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>2.1%</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#f87171' }}>-$0.53</td>
            </tr>
            <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#f87171' }}>7 關</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>89.40</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>$8,940</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>1.1%</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#f87171' }}>-$1.01</td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#f87171' }}>8 關</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>169.86</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>$16,986</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>0.6%</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#f87171' }}>-$1.92</td>
            </tr>
            <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#f87171' }}>10 關</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>613.11</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>$61,311</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>0.16%</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#f87171' }}>-$6.93</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: 8, padding: 16, margin: '24px 0' }}>
        <p style={{ fontWeight: 'bold', color: '#d4af37', marginBottom: 8 }}>關鍵發現</p>
        <ul style={{ margin: 0 }}>
          <li>串關數每增加 1 關，報酬約翻倍，但勝率減半</li>
          <li>期望值隨關數增加而遞減——串越多關，莊家抽水效果越明顯</li>
          <li>2-3 關的 EV 損失最小，是最「划算」的串關範圍</li>
          <li>6 關以上的串關勝率低於 2%，接近樂透性質</li>
        </ul>
      </div>

      {/* ===== 第四章（新增） ===== */}
      <h2 id="calculation-examples" style={{ color: '#d4af37', marginTop: 48 }}>四、串關計算實例：2 串 1 到 5 串 1</h2>

      <p>
        理解原理後，讓我們用真實賽事的賠率來做計算練習。以下每個範例都可以在
        <Link to="/tools/parlay-calculator" style={{ color: '#93c5fd' }}>串關計算器</Link>中驗證。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>範例 A：2 串 1（最穩健）</h3>
      <div style={{ background: '#111', border: '1px solid #333', borderRadius: 8, padding: 16, margin: '16px 0' }}>
        <p style={{ fontWeight: 'bold', color: '#d4af37', marginBottom: 8 }}>場次設定</p>
        <ul style={{ margin: 0 }}>
          <li>MLB 洋基 vs 紅襪：洋基讓 1.5 分，賠率 1.75</li>
          <li>NBA 湖人 vs 太陽：湖人讓 4.5 分，賠率 1.85</li>
        </ul>
        <p style={{ margin: '12px 0 0 0', fontFamily: 'monospace' }}>串關總賠率 = 1.75 x 1.85 = 3.238</p>
        <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace' }}>投注 NT$500 → 全中可得 NT$1,619</p>
        <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace' }}>預估勝率 = (1/1.75) x (1/1.85) = 57.1% x 54.1% = 30.9%</p>
        <p style={{ margin: '4px 0 0 0', color: '#4ade80' }}>分析：2 串 1 勝率約 3 成，風險可控，適合穩健型玩家</p>
      </div>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>範例 B：3 串 1（甜蜜點）</h3>
      <div style={{ background: '#111', border: '1px solid #333', borderRadius: 8, padding: 16, margin: '16px 0' }}>
        <p style={{ fontWeight: 'bold', color: '#d4af37', marginBottom: 8 }}>場次設定</p>
        <ul style={{ margin: 0 }}>
          <li>NBA 塞爾提克大分 218.5，賠率 1.90</li>
          <li>MLB 道奇勝，賠率 1.65</li>
          <li>英超利物浦勝，賠率 1.50</li>
        </ul>
        <p style={{ margin: '12px 0 0 0', fontFamily: 'monospace' }}>串關總賠率 = 1.90 x 1.65 x 1.50 = 4.703</p>
        <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace' }}>投注 NT$300 → 全中可得 NT$1,411</p>
        <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace' }}>預估勝率 = 52.6% x 60.6% x 66.7% = 21.3%</p>
        <p style={{ margin: '4px 0 0 0', color: '#4ade80' }}>分析：混合低賠率場次可拉高整體勝率，4.7 倍報酬仍然有感</p>
      </div>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>範例 C：4 串 1（進階玩家）</h3>
      <div style={{ background: '#111', border: '1px solid #333', borderRadius: 8, padding: 16, margin: '16px 0' }}>
        <p style={{ fontWeight: 'bold', color: '#d4af37', marginBottom: 8 }}>場次設定</p>
        <ul style={{ margin: 0 }}>
          <li>NBA 勇士讓 6.5 分，賠率 1.90</li>
          <li>NBA 公鹿大分 225.5，賠率 1.85</li>
          <li>MLB 大谷翔平三振 7.5 以上，賠率 1.80</li>
          <li>英超曼城勝，賠率 1.40</li>
        </ul>
        <p style={{ margin: '12px 0 0 0', fontFamily: 'monospace' }}>串關總賠率 = 1.90 x 1.85 x 1.80 x 1.40 = 8.857</p>
        <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace' }}>投注 NT$200 → 全中可得 NT$1,771</p>
        <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace' }}>預估勝率 = 52.6% x 54.1% x 55.6% x 71.4% = 11.3%</p>
        <p style={{ margin: '4px 0 0 0', color: '#fbbf24' }}>分析：4 關勝率已降到約 1 成，但加入一場低賠率場次（1.40）可有效提升整體勝率</p>
      </div>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>範例 D：5 串 1（高風險高報酬）</h3>
      <div style={{ background: '#111', border: '1px solid #333', borderRadius: 8, padding: 16, margin: '16px 0' }}>
        <p style={{ fontWeight: 'bold', color: '#d4af37', marginBottom: 8 }}>場次設定（全部賠率 1.90）</p>
        <ul style={{ margin: 0 }}>
          <li>NBA 場次 1：讓分盤，賠率 1.90</li>
          <li>NBA 場次 2：大小分，賠率 1.90</li>
          <li>NBA 場次 3：讓分盤，賠率 1.90</li>
          <li>MLB 場次 4：讓分盤，賠率 1.90</li>
          <li>MLB 場次 5：大小分，賠率 1.90</li>
        </ul>
        <p style={{ margin: '12px 0 0 0', fontFamily: 'monospace' }}>串關總賠率 = 1.90^5 = 24.761</p>
        <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace' }}>投注 NT$100 → 全中可得 NT$2,476</p>
        <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace' }}>預估勝率 = 52.6%^5 = 4.0%</p>
        <p style={{ margin: '4px 0 0 0', color: '#f87171' }}>分析：100 元搏近 2,500 元看似誘人，但 96% 的機率會全輸——這就是串關的真相</p>
      </div>

      {/* ===== 第五章 ===== */}
      <h2 id="optimal-strategy" style={{ color: '#d4af37', marginTop: 48 }}>五、最佳串關組合策略</h2>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>5.1 黃金法則：2-3 關為主</h3>
      <p>
        從 EV 和勝率的角度來看，2-3 關串關是最佳甜蜜點——
        報酬足夠吸引人（3.6x - 6.9x），勝率也還在合理範圍（15-28%）。
        超過 4 關以上的串關，莊家的抽水效果會累積放大，EV 快速下降。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>5.2 選擇高信心的比賽</h3>
      <p>
        串關的致命弱點是「一場輸全輸」。因此每一場比賽的選擇都要非常慎重。
        建議只串你真正有信心的比賽，不要為了湊數而加入不確定的場次。
        如果只找到 2 場有把握的比賽，就串 2 關——不要為了串 3 關而硬加一場。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>5.3 避開高賠率陷阱</h3>
      <p>
        有些人喜歡串高賠率的比賽（如 3.0 以上），認為可以用少數場次獲得高報酬。
        但高賠率代表該結果的機率較低，串在一起時勝率會急劇下降。
        用計算器輸入不同賠率組合，你會清楚看到勝率的差異。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>5.4 混合不同運動、不同玩法</h3>
      <p>
        如果所有串關場次都是同一項運動（例如全是 NBA），
        風險會因為聯盟整體趨勢（例如某天全聯盟大爆冷）而集中。
        建議混合不同運動（NBA + MLB + 足球），分散系統性風險。
        也可以混合讓分盤和大小分，降低全輸的機率。
        想深入了解各種投注類型，可以參考<Link to="/blog/handicap-betting-guide" style={{ color: '#93c5fd' }}>讓分盤完整教學</Link>和<Link to="/blog/over-under-betting-guide" style={{ color: '#93c5fd' }}>大小分投注指南</Link>。
      </p>

      {/* ===== 第六章（新增） ===== */}
      <h2 id="risk-management" style={{ color: '#d4af37', marginTop: 48 }}>六、串關風險管理</h2>

      <p>
        串關的高波動性質決定了它需要嚴格的資金管理。以下是經過驗證的風險管理原則：
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>6.1 固定投注比例法</h3>
      <p>
        每次串關的投注金額不超過<strong>總資金的 1-2%</strong>。
        例如你有 NT$10,000 的運彩資金，每次串關最多投注 NT$100-200。
        這確保即使連續 10 次全輸（在串關中很常見），你仍有 80% 以上的資金繼續操作。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>6.2 每週停損機制</h3>
      <p>
        設定每週串關的總預算上限。建議不超過總資金的 10%。
        例如總資金 NT$10,000，每週串關預算為 NT$1,000。
        不管輸贏，預算用完就停。這能避免「追回本金」的衝動性投注。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>6.3 贏利管理：達標即收</h3>
      <p>
        串關中獎後，建議將贏利的 50% 以上提領出來或轉入「不動用」帳戶。
        避免把串關贏來的錢全部再投入串關——因為長期 EV 為負，繼續串下去只會把贏利還回去。
        資金管理的完整策略，推薦閱讀<Link to="/blog/bankroll-management-guide" style={{ color: '#93c5fd' }}>資金管理完全指南</Link>。
      </p>

      <div style={{ overflowX: 'auto', margin: '16px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#1e40af', color: '#fff' }}>
              <th style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>資金規模</th>
              <th style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>單次串關上限</th>
              <th style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>每週預算</th>
              <th style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>建議關數</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$5,000</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$50-100</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$500</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#4ade80' }}>2 關</td>
            </tr>
            <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$10,000</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$100-200</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$1,000</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#4ade80' }}>2-3 關</td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$30,000</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$300-600</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$3,000</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#fbbf24' }}>2-3 關</td>
            </tr>
            <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$50,000+</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$500-1,000</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$5,000</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#fbbf24' }}>2-4 關</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ===== 第七章（新增） ===== */}
      <h2 id="parlay-vs-single" style={{ color: '#d4af37', marginTop: 48 }}>七、串關 vs 單場投注的數學比較</h2>

      <p>
        許多玩家好奇：同樣的比賽，串關和分開買單場，到底哪個比較好？讓我們用數學來說話。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>情境：3 場比賽，各賠率 1.90</h3>

      <div style={{ overflowX: 'auto', margin: '16px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#1e40af', color: '#fff' }}>
              <th style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'left' }}>比較項目</th>
              <th style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>3 串 1（投 NT$300）</th>
              <th style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>3 場單買（各 NT$100）</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ border: '1px solid #333', padding: '10px 14px' }}>總投注金額</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$300</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$300</td>
            </tr>
            <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
              <td style={{ border: '1px solid #333', padding: '10px 14px' }}>全中時回收</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#4ade80' }}>NT$2,058（6.86x）</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$570（3 x 190）</td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #333', padding: '10px 14px' }}>中 2 場時回收</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#f87171' }}>NT$0（全輸）</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#4ade80' }}>NT$380（2 x 190）</td>
            </tr>
            <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
              <td style={{ border: '1px solid #333', padding: '10px 14px' }}>中 1 場時回收</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#f87171' }}>NT$0（全輸）</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$190</td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #333', padding: '10px 14px' }}>全輸時損失</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$300</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center' }}>NT$300</td>
            </tr>
            <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
              <td style={{ border: '1px solid #333', padding: '10px 14px' }}>期望值（EV）</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#f87171' }}>-NT$0.24</td>
              <td style={{ border: '1px solid #333', padding: '10px 14px', textAlign: 'center', color: '#fbbf24' }}>-NT$0.09</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: 8, padding: 16, margin: '24px 0' }}>
        <p style={{ fontWeight: 'bold', color: '#d4af37', marginBottom: 8 }}>結論</p>
        <ul style={{ margin: 0 }}>
          <li><strong>追求最大報酬：</strong>串關的全中回報（2,058 元）遠超單場（570 元）</li>
          <li><strong>追求穩定獲利：</strong>單場投注在中 2 場或 1 場時仍有部分回收，串關則全數歸零</li>
          <li><strong>數學上最佳：</strong>單場投注的 EV 損失（-$0.09）小於串關（-$0.24）</li>
          <li><strong>建議：</strong>將 80% 資金用於單場投注確保穩定，20% 用於小額串關追求刺激</li>
        </ul>
      </div>

      <InlineCTA />

      {/* ===== 第八章 ===== */}
      <h2 id="risk-analysis" style={{ color: '#d4af37', marginTop: 48 }}>八、風險分析：關數越多越危險</h2>

      <p>
        為什麼說串關「關數越多越虧」？因為每多串一關，莊家的抽水就多累積一次。
      </p>

      <div style={{ background: '#111', border: '1px solid #333', borderRadius: 8, padding: 16, margin: '16px 0', fontFamily: 'monospace', fontSize: 14 }}>
        <p style={{ margin: 0, color: '#fbbf24' }}>莊家抽水累積效果：</p>
        <p style={{ margin: '8px 0 0 0' }}>單場賠率 1.90，公平賠率應為 2.00</p>
        <p style={{ margin: '4px 0 0 0' }}>莊家抽水 = (2.00 - 1.90) / 2.00 = 5%</p>
        <p style={{ margin: '12px 0 0 0' }}>2 關累積抽水：1 - (0.95)^2 = 9.75%</p>
        <p style={{ margin: '4px 0 0 0' }}>5 關累積抽水：1 - (0.95)^5 = 22.62%</p>
        <p style={{ margin: '4px 0 0 0' }}>10 關累積抽水：1 - (0.95)^10 = 40.13%</p>
        <p style={{ margin: '12px 0 0 0', color: '#f87171' }}>10 關串關時，莊家累積抽走了約 40% 的期望值！</p>
      </div>

      <p>
        這也是為什麼運彩公司最喜歡推廣串關——關數越多，他們的利潤越高。
        玩家看到的是「100 元變 6 萬」的誘惑，但數學告訴我們的是「99.84% 的機率會全輸」。
      </p>

      <p>
        換一個角度思考：如果你每天花 NT$100 串 10 關，平均要連續串約 625 天才會中一次。
        即使中了拿回 NT$61,311，你在這 625 天已經投入 NT$62,500——最終仍是虧損的。
        這就是「大數法則」的威力，莊家靠的就是這個數學優勢。
      </p>

      {/* ===== 第九章（新增） ===== */}
      <h2 id="common-mistakes" style={{ color: '#d4af37', marginTop: 48 }}>九、常見串關錯誤</h2>

      <p>
        以下是新手最常犯的串關錯誤：
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>錯誤 1：為了湊數硬串</h3>
      <p>
        只找到 2 場有把握的比賽，但為了讓賠率更高而硬加第 3 場不熟悉的比賽。
        結果往往就是那場不熟悉的比賽輸了，連帶前 2 場的正確判斷也變成廢紙。
        <strong>寧可串 2 關，也不要硬串 3 關。</strong>
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>錯誤 2：只看賠率不看勝率</h3>
      <p>
        看到某場比賽賠率 4.00 就興奮地串進去，心想「中了就翻四倍」。
        但 4.00 的賠率代表隱含勝率只有 25%。串進 3 關串後，
        整體勝率可能從 15% 直接跌到 4%——你是在用勝率換賠率，並不划算。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>錯誤 3：連續加碼追回</h3>
      <p>
        串關輸了之後加倍投注想追回，這是最危險的行為。
        假設你用 NT$100 串 3 關，輸了後加碼到 NT$200，再輸加到 NT$400...
        5 次全輸後你已經投入 NT$3,100，而且勝率依然只有 15%。
        串關不適合使用加倍策略——每次都是獨立事件，前一次輸了不代表下一次更容易中。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>錯誤 4：同場比賽串不同玩法</h3>
      <p>
        把同一場比賽的讓分和大小分串在一起看似增加了關數，
        但這兩個結果之間可能存在相關性（例如一面倒的比賽往往同時讓分過關且大分），
        相關性會降低串關的分散效果。建議盡量串不同場次的比賽。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>錯誤 5：忽略即時盤口變動</h3>
      <p>
        賽前看到的賠率和下注時的賠率可能已經不同。
        特別是 MLB 先發投手更換、NBA 主力球員傷退等消息，
        都會影響盤口。下注前務必確認即時賠率，而不是用計算器算出的舊賠率下注。
        運彩即時分析的方法，可以參考<Link to="/blog/live-betting-strategy" style={{ color: '#93c5fd' }}>滾球投注策略指南</Link>。
      </p>

      {/* ===== 第十章 ===== */}
      <h2 id="examples" style={{ color: '#d4af37', marginTop: 48 }}>十、實戰計算範例</h2>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>範例 1：NBA 3 關串</h3>
      <div style={{ background: '#111', border: '1px solid #333', borderRadius: 8, padding: 16, margin: '16px 0' }}>
        <p style={{ fontWeight: 'bold', color: '#d4af37', marginBottom: 8 }}>場次設定</p>
        <ul style={{ margin: 0 }}>
          <li>湖人讓 3.5 分，賠率 1.85</li>
          <li>塞爾提克大分 220.5，賠率 1.90</li>
          <li>勇士讓 5.5 分，賠率 1.95</li>
        </ul>
        <p style={{ margin: '12px 0 0 0', fontFamily: 'monospace' }}>串關總賠率 = 1.85 x 1.90 x 1.95 = 6.853</p>
        <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace' }}>投注 NT$200 → 全中可得 NT$1,371</p>
        <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace' }}>預估勝率 = 約 14.6%</p>
      </div>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>範例 2：混合賠率 2 關串</h3>
      <div style={{ background: '#111', border: '1px solid #333', borderRadius: 8, padding: 16, margin: '16px 0' }}>
        <p style={{ fontWeight: 'bold', color: '#d4af37', marginBottom: 8 }}>場次設定</p>
        <ul style={{ margin: 0 }}>
          <li>足球冠軍聯賽：皇馬勝，賠率 1.50</li>
          <li>NBA：公鹿讓 7.5 分，賠率 1.90</li>
        </ul>
        <p style={{ margin: '12px 0 0 0', fontFamily: 'monospace' }}>串關總賠率 = 1.50 x 1.90 = 2.85</p>
        <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace' }}>投注 NT$500 → 全中可得 NT$1,425</p>
        <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace' }}>預估勝率 = 約 35.1%</p>
        <p style={{ margin: '4px 0 0 0', color: '#4ade80' }}>低賠率場次（1.50）拉高了整體勝率，適合穩健型玩家</p>
      </div>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>範例 3：跨運動 3 關串（進階）</h3>
      <div style={{ background: '#111', border: '1px solid #333', borderRadius: 8, padding: 16, margin: '16px 0' }}>
        <p style={{ fontWeight: 'bold', color: '#d4af37', marginBottom: 8 }}>場次設定</p>
        <ul style={{ margin: 0 }}>
          <li>NBA 金塊讓 8.5 分，賠率 1.95</li>
          <li>MLB 道奇 -1.5 分，賠率 2.10</li>
          <li>英超阿森納勝，賠率 1.60</li>
        </ul>
        <p style={{ margin: '12px 0 0 0', fontFamily: 'monospace' }}>串關總賠率 = 1.95 x 2.10 x 1.60 = 6.552</p>
        <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace' }}>投注 NT$200 → 全中可得 NT$1,310</p>
        <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace' }}>預估勝率 = 51.3% x 47.6% x 62.5% = 15.3%</p>
        <p style={{ margin: '4px 0 0 0', color: '#4ade80' }}>跨運動串關的好處是分散單一聯盟的風險（例如某天 NBA 全面爆冷）</p>
      </div>

      {/* ===== FAQ ===== */}
      <h2 id="faq" style={{ color: '#d4af37', marginTop: 48 }}>常見問題 FAQ</h2>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>Q1：串關和單場投注哪個比較好？</h3>
      <p>
        從期望值（EV）角度來看，單場投注的 EV 永遠優於串關，因為莊家抽水只算一次。
        串關的優勢在於可以用小本金搏大回報。如果你追求穩定，選單場；
        如果你追求刺激且能承受全輸的風險，可以小額串關。
        建議的比例是 80% 資金買單場、20% 資金玩串關。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>Q2：串關有辦法保證獲利嗎？</h3>
      <p>
        沒有。串關的每一場都是獨立事件，且所有投注都是負期望值。
        任何宣稱能「保證串關獲利」的方法或系統都是不可信的。
        你能做的是選擇 EV 損失最小的組合（2-3 關、避免過高賠率）。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>Q3：台灣運彩的串關規則有什麼特別的？</h3>
      <p>
        台灣運彩的串關最多可串 10 關，最低投注金額為 NT$100。
        部分同一場比賽的不同玩法（如讓分和大小分）可以串在一起，
        但同一場比賽的相同玩法不能重複串關。詳細規則請參考
        <Link to="/blog/sports-betting-guide" style={{ color: '#93c5fd' }}>運彩入門完整指南</Link>。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>Q4：計算器顯示的勝率準確嗎？</h3>
      <p>
        計算器的勝率是根據賠率反推的「隱含機率」，代表莊家認為的合理勝率。
        實際勝率可能更高或更低——如果你認為自己的判斷比莊家更準，
        那就存在所謂的「價值投注」機會。但請注意，莊家的賠率通常非常精準。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>Q5：有什麼串關的資金管理建議？</h3>
      <p>
        串關投注金額建議不超過總資金的 1-2%。
        因為串關的全輸機率很高，需要有足夠的資金承受連續多次全輸。
        把串關當作「小額博大」的娛樂，而不是主要投注策略。
        更多資金管理的建議，請參考<Link to="/blog/parlay-betting-strategy" style={{ color: '#93c5fd' }}>串關策略完整攻略</Link>。
      </p>

      <h3 style={{ color: '#d4af37', marginTop: 32 }}>Q6：串關中獎後該怎麼處理贏利？</h3>
      <p>
        建議至少將贏利的 50% 提領出來，不要全部再投入串關。
        串關的長期 EV 為負，如果把贏利不斷回投，最終大概率會全部輸回去。
        正確的做法是：贏利一部分提領「落袋為安」，一部分轉為單場投注的資金，
        只留小比例繼續串關娛樂。想了解更多體育投注的思維，
        推薦閱讀<Link to="/blog/sports-betting-analysis" style={{ color: '#93c5fd' }}>運彩分析完整教學</Link>。
      </p>

      {/* ===== 結語 ===== */}
      <h2 style={{ color: '#d4af37', marginTop: 48 }}>結語</h2>
      <p>
        串關計算器幫助你在下注前就看清楚報酬和風險的全貌。
        記住幾個關鍵數字：2-3 關是最佳甜蜜點、5 關以上勝率低於 4%、
        10 關串關莊家累積抽水達 40%。掌握這些數據，你就能做出更理性的串關決策。
      </p>
      <p>
        現在就試試<Link to="/tools/parlay-calculator" style={{ color: '#93c5fd' }}>免費串關計算器</Link>，
        輸入你想串的比賽，看看數字告訴你什麼。
        如果想進一步學習運彩投注的完整策略，
        推薦從<Link to="/blog/sports-betting-guide" style={{ color: '#93c5fd' }}>運彩入門完整指南</Link>開始。
      </p>

      <p style={{ color: '#999', fontSize: 13, marginTop: 32 }}>
        免責聲明：本文僅供資訊參考與教學用途，不構成任何投注建議。
        串關計算器的結果基於數學模型，不保證實際投注結果。
        運動投注存在財務風險，請遵守當地法律，理性投注。
        如遇可疑情況，請撥打<a href="https://165.npa.gov.tw" target="_blank" rel="noopener noreferrer" style={{ color: '#93c5fd' }}>165 反詐騙專線</a>諮詢。
      </p>

      {/* ===== 負責任博弈聲明 ===== */}
      <div style={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 12, padding: '20px', marginTop: 48 }}>
        <p style={{ color: '#ef4444', fontWeight: 'bold', fontSize: 16, marginBottom: 12 }}>負責任博弈聲明</p>
        <p style={{ color: '#bbb', fontSize: 14, margin: 0 }}>
          線上博弈涉及財務風險，請量力而為。未滿 18 歲不得參與。如果你或身邊的人出現問題賭博行為，
          請撥打衛生福利部心理健康專線 1925（安心專線）。
          遇到疑似詐騙行為，請立即撥打{' '}
          <a href="https://165.npa.gov.tw" target="_blank" rel="noopener noreferrer" style={{ color: '#93c5fd' }}>165 反詐騙專線</a>。
        </p>
      </div>

    </article>
  );
}
